import { Box, Typography } from '@mui/material';
import React from 'react';

// Import types from local pricing types
import type { MarketplaceService } from '../../types/pricing';

interface SimplePricingDisplayProps {
  service: MarketplaceService;
  className?: string;
}

/**
 * Simple pricing display for marketplace cards - shows only default pricing, no selection UI
 */
export const SimplePricingDisplay: React.FC<SimplePricingDisplayProps> = ({
  service,
  className,
}) => {
  const getPricingDisplay = () => {
    let price = '0.00';
    let suffix = '';
    let additionalFees: string[] = [];

    if (service.pricing_model === 'variants' && service.variants && service.default_variant_key) {
      // Find the default variant
      const defaultVariant = service.variants.variants.find(
        (v) => v.key === service.default_variant_key,
      );
      if (defaultVariant) {
        // Check if service has contract terms and use term-specific pricing
        let pricing = defaultVariant.pricing;
        const hasContractTerms =
          service.contract_term_months && service.contract_term_months.length > 0;
        const defaultTerm =
          service.default_term_months ||
          (hasContractTerms ? service.contract_term_months[0] : null);

        if (hasContractTerms && defaultTerm && defaultVariant.term_overrides) {
          // Use term-specific pricing for contract services
          const termOverride = defaultVariant.term_overrides.find(
            (override: any) => override.term_months === defaultTerm,
          );
          if (termOverride) {
            pricing = termOverride;
          }
        }

        // Handle ranges in variant pricing
        if (
          pricing.min_recurring_cents !== undefined &&
          pricing.max_recurring_cents !== undefined
        ) {
          const minPrice = (pricing.min_recurring_cents / 100).toFixed(2);
          const maxPrice = (pricing.max_recurring_cents / 100).toFixed(2);
          price = `${minPrice} - ${maxPrice}`;
        } else {
          price = (pricing.recurring_cents / 100).toFixed(2);
        }

        suffix =
          pricing.frequency === 'monthly'
            ? '/mo'
            : pricing.frequency === 'quarterly'
              ? '/quarter'
              : pricing.frequency === 'annually'
                ? '/year'
                : pricing.frequency === 'one_time'
                  ? ' one time charge'
                  : '';

        // Collect additional fees
        if (pricing.nrc_cents > 0) {
          additionalFees.push(`Setup: $${(pricing.nrc_cents / 100).toFixed(2)}`);
        }
        if (pricing.shipping_cents > 0) {
          additionalFees.push(`Shipping: $${(pricing.shipping_cents / 100).toFixed(2)}`);
        }
        if (pricing.other_cents > 0) {
          additionalFees.push(`Other: $${(pricing.other_cents / 100).toFixed(2)}`);
        }
      }
    } else if (
      service.pricing_model === 'multi_frequency' &&
      service.frequencies &&
      service.frequencies.length > 0
    ) {
      // Show the first (usually monthly) frequency
      const freq = service.frequencies[0];
      price = (freq.recurring_cents / 100).toFixed(2);
      suffix =
        freq.frequency === 'monthly'
          ? '/mo'
          : freq.frequency === 'quarterly'
            ? '/quarter'
            : freq.frequency === 'annually'
              ? '/year'
              : freq.frequency === 'one_time'
                ? ' one time charge'
                : '';

      // Collect additional fees
      if (freq.nrc_cents > 0) {
        additionalFees.push(`Setup: $${(freq.nrc_cents / 100).toFixed(2)}`);
      }
      if (freq.shipping_cents > 0) {
        additionalFees.push(`Shipping: $${(freq.shipping_cents / 100).toFixed(2)}`);
      }
      if (freq.other_cents > 0) {
        additionalFees.push(`Other: $${(freq.other_cents / 100).toFixed(2)}`);
      }
    } else if (service.default_pricing) {
      // Handle varying pricing (ranges)
      if (
        service.default_pricing.min_recurring_cents !== undefined &&
        service.default_pricing.max_recurring_cents !== undefined
      ) {
        const minPrice = (service.default_pricing.min_recurring_cents / 100).toFixed(2);
        const maxPrice = (service.default_pricing.max_recurring_cents / 100).toFixed(2);
        price = `${minPrice} - ${maxPrice}`;
        suffix =
          service.default_pricing.frequency === 'monthly'
            ? '/mo'
            : service.default_pricing.frequency === 'quarterly'
              ? '/quarter'
              : service.default_pricing.frequency === 'annually'
                ? '/year'
                : service.default_pricing.frequency === 'one_time'
                  ? ' one time charge'
                  : '';

        // Collect additional fees
        if (service.default_pricing.nrc_cents > 0) {
          additionalFees.push(`Setup: $${(service.default_pricing.nrc_cents / 100).toFixed(2)}`);
        }
        if (service.default_pricing.shipping_cents > 0) {
          additionalFees.push(
            `Shipping: $${(service.default_pricing.shipping_cents / 100).toFixed(2)}`,
          );
        }
        if (service.default_pricing.other_cents > 0) {
          additionalFees.push(`Other: $${(service.default_pricing.other_cents / 100).toFixed(2)}`);
        }
      } else {
        // Fixed pricing
        price = (service.default_pricing.recurring_cents / 100).toFixed(2);
        suffix =
          service.default_pricing.frequency === 'monthly'
            ? '/mo'
            : service.default_pricing.frequency === 'quarterly'
              ? '/quarter'
              : service.default_pricing.frequency === 'annually'
                ? '/year'
                : service.default_pricing.frequency === 'one_time'
                  ? ' one time charge'
                  : '';

        // Collect additional fees
        if (service.default_pricing.nrc_cents > 0) {
          additionalFees.push(`Setup: $${(service.default_pricing.nrc_cents / 100).toFixed(2)}`);
        }
        if (service.default_pricing.shipping_cents > 0) {
          additionalFees.push(
            `Shipping: $${(service.default_pricing.shipping_cents / 100).toFixed(2)}`,
          );
        }
        if (service.default_pricing.other_cents > 0) {
          additionalFees.push(`Other: $${(service.default_pricing.other_cents / 100).toFixed(2)}`);
        }
      }
    } else if (service.base_price !== undefined && service.base_price !== null) {
      const numericBasePrice = typeof service.base_price === 'number'
        ? service.base_price
        : Number(service.base_price);
      if (!Number.isNaN(numericBasePrice) && numericBasePrice > 0) {
        price = numericBasePrice.toFixed(2);
        suffix = '/mo';
      }
    }

    return { price, suffix, additionalFees };
  };

  const pricing = getPricingDisplay();
  let prefix = '';
  if (service.pricing_model === 'variants' && service.variants && service.variants.variants.length > 1) {
    const prices = service.variants.variants.map(v => v.pricing.recurring_cents);
    const unique = new Set(prices);
    if (unique.size > 1) prefix = 'From ';
  }

  return (
    <Box className={className}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 'bold',
          color: 'text.primary',
          mb: 1,
        }}
      >
        {prefix}${pricing.price}{pricing.suffix}
      </Typography>

      {/* Show additional fees if any exist */}
      {pricing.additionalFees.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {pricing.additionalFees.map((fee, index) => (
            <Typography
              key={index}
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.7rem', display: 'block' }}
            >
              {fee}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};
